import type { ReactNode } from 'react';
import { Image } from '@/components';
import {
    PopoverPositioner,
    PopoverRoot,
    PopoverTrigger,
} from '@/components/Popover';
import { cn } from '@/lib/utils';

interface ThumbnailWithPreviewProps {
    imageUrl: string | null;
    name: string;
    fallbackIcon: ReactNode;
}

export function ThumbnailWithPreview({
    imageUrl,
    name,
    fallbackIcon,
}: ThumbnailWithPreviewProps) {
    const thumbnail = (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-grey-500">
            {imageUrl ? (
                <Image
                    src={imageUrl}
                    alt={name}
                    auth
                    className="h-full w-full object-cover"
                />
            ) : (
                fallbackIcon
            )}
        </div>
    );

    if (!imageUrl) {
        return thumbnail;
    }

    return (
        <PopoverRoot>
            <PopoverTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    aria-label={name}
                    className={cn(
                        'flex h-14 w-14 shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-md bg-grey-500 outline-none focus-visible:ring-2 focus-visible:ring-grey-400 focus-visible:ring-offset-2 focus-visible:ring-offset-grey-900',
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                        }
                    }}
                >
                    <Image
                        src={imageUrl}
                        alt={name}
                        auth
                        className="h-full w-full object-cover"
                    />
                </div>
            </PopoverTrigger>
            <PopoverPositioner
                side="right"
                sideOffset={8}
                className="rounded-md border border-grey-500 bg-grey-700 p-1 shadow-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="h-52 w-52 overflow-hidden rounded-md">
                    <Image
                        src={imageUrl}
                        alt={name}
                        auth
                        className="h-full w-full object-cover"
                    />
                </div>
            </PopoverPositioner>
        </PopoverRoot>
    );
}
